import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import * as actions from './actions';
import { selectors } from './reducer';
import { RXGateService } from './service';
import {
  ApplyRecipePayload,
  CheckProvisionPayload,
  GetRecipeInfoPayload,
  GetSuitableMedicationsPayload,
  RxGateCurrentReceiptData,
  RxGateReceiptsResponseData,
} from './types';
import { rxGateFormatReceipt, rxGateformatRecipeData } from './helpers';

// Унифицированный разбор ошибок API/axios, чтобы в стор шла строка.
const extractErrorMessage = (
  errorLike: any,
  fallback = 'Не удалось получить данные рецепта'
) => {
  const reqErr = errorLike?.request_error;
  const errInfo = reqErr?.error_info;
  const code = reqErr?.error_code ?? reqErr?.code;
  // ALO-ошибки показываем через справочник.
  if (code === 'ALO_ERROR') {
    return errInfo?.payload?.message || fallback;
  }
  if (code === 'VALIDATION_ERROR') {
    return 'Ошибка. Поля заполнены некорректно';
  }
  const msg =
    errInfo?.error_message ||
    errInfo?.message ||
    errInfo?.error ||
    errorLike?.error ||
    errorLike?.message ||
    errorLike?.detail ||
    fallback;
  const safeMsg = msg || fallback;
  return code ? `${safeMsg} [${code}]` : safeMsg;
};

interface GetRecipeInfoSagaProps {
  type: string;
  payload: GetRecipeInfoPayload;
}

interface GetSuitableMedicationsSagaProps {
  type: string;
  payload: GetSuitableMedicationsPayload;
}

interface CheckProvisionSagaProps {
  type: string;
  payload: CheckProvisionPayload;
}

interface ApplyRecipeSagaProps {
  type: string;
  payload: ApplyRecipePayload;
}

export function* getRecipeInfoSaga({ payload }: GetRecipeInfoSagaProps): any {
  try {
    // Ответ может содержать recipe или request_error; оставляем any.
    const response: any = yield call(RXGateService.getRecipeInfo, payload);
    if (response?.request_error) {
      yield put(actions.getRecipeInfo.error(extractErrorMessage(response)));
      return;
    }
    // 401/иные ошибки без request_error, но с полями code/error/message.
    if (response?.code || response?.status === 'error') {
      const msg = response.error || response.message || 'Не авторизован';
      yield put(
        actions.getRecipeInfo.error(`${msg} [${response.code ?? 'UNKNOWN'}]`)
      );
      return;
    }

    const resData = rxGateformatRecipeData(response.recipe);
    yield put(actions.getRecipeInfo.success(resData));
  } catch (error) {
    const data = (error as any)?.response?.data || error;
    yield put(actions.getRecipeInfo.error(extractErrorMessage(data)));
  }
}

export function* getSuitableMedicationsSaga({
  payload,
}: GetSuitableMedicationsSagaProps): any {
  try {
    const response: any = yield call(
      RXGateService.getSuitableMedications,
      payload
    );

    yield put(actions.getSuitableMedications.success(response.products));
  } catch (error) {
    const data = (error as any)?.response?.data || error;
    yield put(actions.getSuitableMedications.error(data));
  }
}

export function* checkProvisionSaga(): any {
  const recipeID: string = yield select(selectors.recipeNumber);
  const receipt: RxGateCurrentReceiptData = yield select(
    selectors.rxGateChosenReceipt
  );

  try {
    // Ответ может содержать recipe или request_error; оставляем any.
    const response: any = yield call(RXGateService.checkProvision, {
      recipeIsloId: Number(recipeID),
      docID: receipt?.docId ?? '',
    } as CheckProvisionPayload);

    // 401/иные ошибки без request_error, но с полями code/error/message.
    if (response?.code || response?.status === 'error') {
      const msg = response.error || response.message || 'Не авторизован';
      yield put(
        actions.checkProvision.error(`${msg} [${response.code ?? 'UNKNOWN'}]`)
      );
      return;
    }

    yield put(actions.checkProvision.success(response));
  } catch (error) {
    const data = (error as any)?.response?.data || error;
    yield put(actions.checkProvision.error(extractErrorMessage(data)));
  }
}

export function* applyRecipeSaga({ payload }: ApplyRecipeSagaProps): any {
  try {
    // Ответ может содержать recipe или request_error; оставляем any.
    const response: any = yield call(RXGateService.applyRecipe, payload);
    // 401/иные ошибки без request_error, но с полями code/error/message.
    if (response?.code || response?.status === 'error') {
      const msg = response.error || response.message || 'Не авторизован';
      yield put(
        actions.applyRecipe.error(`${msg} [${response.code ?? 'UNKNOWN'}]`)
      );
      return;
    }

    if (payload.postEffect) {
      payload.postEffect();
    }

    yield put(actions.applyRecipe.success(response));
  } catch (error) {
    const data = (error as any)?.response?.data || error;
    yield put(actions.applyRecipe.error(extractErrorMessage(data)));
  }
}

export function* getRxGateCurrentReceiptSaga() {
  const recipeID: string = yield select(selectors.recipeNumber);

  try {
    const res: RxGateReceiptsResponseData = yield call(
      RXGateService.getRxGateCurrentReceipt,
      recipeID
    );
    const formattedReceipt = rxGateFormatReceipt(res);
    yield put(actions.getRxGateCurrentReceipt.success(formattedReceipt));
  } catch (error) {
    yield put(actions.getRxGateCurrentReceipt.error(error));
  }
}

export function* rxGateWatcherSaga() {
  yield all([takeLatest(actions.getRecipeInfo.REQUEST, getRecipeInfoSaga)]);
  yield all([
    takeLatest(
      actions.getSuitableMedications.REQUEST,
      getSuitableMedicationsSaga
    ),
  ]);
  yield all([takeLatest(actions.checkProvision.REQUEST, checkProvisionSaga)]);
  yield all([
    takeLatest(
      actions.getRxGateCurrentReceipt.REQUEST,
      getRxGateCurrentReceiptSaga
    ),
  ]);
  yield all([takeLatest(actions.applyRecipe.REQUEST, applyRecipeSaga)]);
}
